import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, MessageCircle, Unplug, Users } from "lucide-react";

import { ErrorApi } from "../api/cliente.ts";
import { usarDesvincularTelegram, usarEnlaceTelegram, usarTelegram } from "../ganchos/datos.ts";
import { haceRato } from "../formato.ts";
import { Aviso, Boton, Dialogo, Tarjeta, cx } from "../componentes/ui.tsx";

/**
 * Conectar Telegram sin configurar nada (SPEC-023).
 *
 * Lo que esta pantalla evita es un trámite: crear un bot con @BotFather, copiar
 * su token y averiguar el `chat_id` de un grupo, que no aparece en ninguna
 * pantalla de Telegram. Aquí se pulsa un botón y se elige el grupo.
 *
 * No pinta un QR aunque el panel viva en el computador y Telegram en el
 * teléfono: `t.me` ya muestra el suyo cuando el enlace se abre sin la
 * aplicación instalada, y añadir una dependencia para repetir eso sería peor.
 */
export function Telegram() {
  const { data, isPending, error, refetch } = usarTelegram();
  const pedirEnlace = usarEnlaceTelegram();
  const desvincular = usarDesvincularTelegram();
  const [enlace, setEnlace] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  // Mientras la ventana del enlace está abierta se pregunta cada pocos segundos:
  // el vínculo lo crea Telegram, no nosotros, así que no hay nada que esperar
  // salvo que aparezca. En cuanto aparece, la ventana se cierra sola.
  const vinculo = data?.vinculo ?? null;
  useEffect(() => {
    if (!enlace) return;
    const t = setInterval(() => void refetch(), 3000);
    return () => clearInterval(t);
  }, [enlace, refetch]);
  useEffect(() => {
    if (enlace && vinculo) setEnlace(null);
  }, [enlace, vinculo]);

  if (error instanceof ErrorApi && error.esSinPermiso) return null;

  async function copiar() {
    if (!enlace) return;
    try {
      await navigator.clipboard.writeText(enlace);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      setCopiado(false);
    }
  }

  function conectar(grupo: boolean) {
    setEnlace(null);
    pedirEnlace.mutate(grupo, { onSuccess: (r) => setEnlace(r.enlace) });
  }

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-tinta">
          <MessageCircle className="size-5 text-marca" aria-hidden />
          Avisos por Telegram
        </h2>
        <p className="max-w-[68ch] text-sm text-tenue">
          Para que un pago suene en el momento en que entra. No hay que crear ningún bot ni copiar
          ningún código: eliges dónde quieres los avisos y listo.
        </p>
      </header>

      {isPending ? (
        <Tarjeta className="p-5 text-sm text-tenue">Comprobando…</Tarjeta>
      ) : !data?.disponible ? (
        <Aviso tono="aviso" titulo="Todavía no disponible en este servidor">
          Falta configurar el bot de 4drando. Mientras tanto, los avisos quedan en el registro del
          servidor.
        </Aviso>
      ) : vinculo ? (
        <Tarjeta className="flex flex-wrap items-center gap-x-4 gap-y-3 p-5">
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-exito-suave text-exito-tinta">
            <Check className="size-4.5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-2 font-medium text-tinta">
              {vinculo.chat}
              {vinculo.tipo && vinculo.tipo !== "private" && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-tenue">
                  <Users className="size-3.5" aria-hidden />
                  grupo
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs text-tenue-2">Conectado {haceRato(vinculo.desde)}</p>
          </div>
          <Boton
            variante="fantasma"
            tamano="sm"
            icono={Unplug}
            cargando={desvincular.isPending}
            onClick={() => desvincular.mutate()}
          >
            Desconectar
          </Boton>
        </Tarjeta>
      ) : (
        <Tarjeta className="space-y-4 p-5">
          <p className="text-sm text-tenue">¿Dónde quieres que suenen?</p>
          <div className="flex flex-wrap gap-2">
            <Boton
              variante="primario"
              icono={Users}
              cargando={pedirEnlace.isPending}
              onClick={() => conectar(true)}
            >
              En un grupo del equipo
            </Boton>
            <Boton variante="suave" icono={MessageCircle} onClick={() => conectar(false)}>
              Solo a mí
            </Boton>
          </div>
          <p className="text-xs text-tenue-2">
            En un grupo lo ve todo el que esté en él, también quien lo añadan después. Si prefieres
            que nadie más vea montos ni nombres, elige «Solo a mí».
          </p>
          {pedirEnlace.error instanceof ErrorApi && (
            <Aviso titulo="No se pudo generar el enlace">{pedirEnlace.error.message}</Aviso>
          )}
        </Tarjeta>
      )}

      <Dialogo
        abierto={!!enlace}
        alCerrar={() => setEnlace(null)}
        titulo="Abre Telegram para terminar"
        descripcion="Se abrirá Telegram y te preguntará dónde quieres los avisos. Esta ventana se cierra sola en cuanto quede conectado."
        pie={
          <>
            <Boton variante="suave" icono={copiado ? Check : Copy} onClick={() => void copiar()}>
              {copiado ? "Copiado" : "Copiar enlace"}
            </Boton>
            <Boton
              variante="primario"
              icono={ExternalLink}
              onClick={() => enlace && window.open(enlace, "_blank", "noopener")}
            >
              Abrir Telegram
            </Boton>
          </>
        }
      >
        <div className="space-y-3">
          <code className="cifra block overflow-x-auto rounded-xl border border-borde bg-superficie-2 p-3 text-xs break-all text-tinta">
            {enlace}
          </code>
          <p className={cx("text-sm text-tenue")}>
            Si Telegram lo tienes solo en el teléfono, copia el enlace y ábrelo allí — o abre este
            desde el computador y Telegram te mostrará un código para escanear.
          </p>
          <p className="text-xs text-tenue-2">
            El enlace sirve una sola vez y caduca en {pedirEnlace.data?.minutos ?? 15} minutos. Si se
            vence, pides otro.
          </p>
        </div>
      </Dialogo>
    </section>
  );
}
